import LogRocket from "logrocket"
import nanoid from "nanoid"
import { runMode, getRunMode } from "utils-lib/builds"
import { getWindow } from "utils-lib/getWindow"

const ssrWindow = getWindow()

// Flag to track if LogRocket has been initialized
let isLogRocketInitialized = false

/**
 * Determines whether LogRocket should be enabled based on the current run mode.
 * @returns {boolean} True if LogRocket should be enabled in the current environment
 */
export const isShouldEnableLogRocket = () => {
  switch (getRunMode()) {
    // Add here to enable logrocket and it takes `REACT_APP_NODE_ENV`
    // To enable logRocket in local logRocket test use `case runMode.LOCAL:`
    // **** DO NOT COMMIT any changes without consult logrocket admins ****
    // case runMode.LOCAL:
    // case runMode.CPV:
    case runMode.QA:
    case runMode.QA3:
    case runMode.QA1:
    case runMode.PROD: {
      return true
    }
    default: {
      return false
    }
  }
}

/**
 * Checks if LogRocket is both enabled and initialized.
 * @returns {boolean} True if LogRocket is enabled and initialized
 */
export const isLogRocketReady = () =>
  isShouldEnableLogRocket() && isLogRocketInitialized

/**
 * Identifies a user in LogRocket if the service is ready.
 * @param {Object} o - User properties to associate with this session
 * @returns {Promise|undefined} LogRocket identify promise if successful, undefined otherwise
 */
// eslint-disable-next-line consistent-return
export const logRocketIdentify = (o) => {
  if (isLogRocketReady()) {
    return LogRocket.identify(ssrWindow.sessionStorage.getItem(`ID`), o)
  }
}

/**
 * Checks if LogRocket global object already exists and is loaded.
 * @returns {boolean} True if LogRocket is already loaded via script tag
 * @private
 */
const isLogRocketAlreadyLoaded = () =>
  typeof ssrWindow._LRLoaded !== "undefined" && ssrWindow._LRLoaded === true

/**
 * Initializes LogRocket with appropriate configuration if it should be enabled.
 * Sets up session URL copy functionality in non-production environments.
 * @returns {void}
 */
export const logRocketInit = () => {
  // Skip if LogRocket was already initialized or is already loaded via script tag
  if (isLogRocketInitialized || isLogRocketAlreadyLoaded()) {
    return
  }

  const rootHostname = `wm.com`
  const logRocketAppId =
    getRunMode() === runMode.PROD ? `u51vwj/wm-ecom` : `u51vwj/wmcom-qadev`

  if (isShouldEnableLogRocket()) {
    const ID = nanoid(12)

    if (ssrWindow.sessionStorage.getItem(`ID`) === null) {
      ssrWindow.sessionStorage.setItem(`ID`, ID)
    }

    /**
     * LogRocket configuration object.
     * @type {Object}
     * @property {boolean} shouldCaptureIP - Whether to capture the user's IP address
     * @property {string} rootHostname - Root hostname to use for LogRocket
     * @property {Object} network - Network configuration options
     * @property {Function} network.requestSanitizer - Function to sanitize sensitive data from network requests
     */
    try {
      LogRocket.init(logRocketAppId, {
        shouldCaptureIP: true,
        rootHostname,
        network: {
          requestSanitizer: (request) => {
            // if the url contains 'rest-api.wm.com/user/authenticate'
            if (
              request.url
                .toString()
                .toLowerCase()
                .indexOf(`rest-api.wm.com/user/authenticate`) !== -1
            ) {
              // scrub out the password
              request.body = null
            }
            return request
          },
        },
      })

      // Mark LogRocket as initialized
      isLogRocketInitialized = true
      ssrWindow._LRLoaded = true

      // This runs and captures logrocket session url to clipboard only in lower environments.
      // To copy the logrocket url for current session , type `ctrl + l` on the browser.
      if (getRunMode() !== runMode.PROD) {
        LogRocket.getSessionURL((sessionURL) => {
          ssrWindow.addEventListener("keydown", ({ ctrlKey, key }) => {
            if (ctrlKey && key === "l") {
              navigator.clipboard
                .writeText(sessionURL)
                // eslint-disable-next-line no-alert
                .then(() => alert("Logrocket session URL copied to clipboard"))
                .catch(console.error)
            }
          })
        })
      }
    } catch (error) {
      console.warn("Failed to initialize LogRocket:", error)
    }
  }
}

/**
 * Tracks a custom event in LogRocket if the service is ready.
 * @param {string} customEventString - The name of the event to be tracked
 * @returns {void}
 */
export const logRocketTrack = (customEventString) => {
  if (isLogRocketReady()) {
    LogRocket.track(customEventString)
  }
}

/**
 * Tracks a custom event with properties in LogRocket if the service is ready.
 * @param {string} eventName - The name of the event to be tracked
 * @param {Object} eventProperties - Additional properties to associate with the event
 * @returns {void}
 */
export const logRocketTrackwithObject = (eventName, eventProperties) => {
  if (isLogRocketReady()) {
    LogRocket.track(eventName, eventProperties)
  }
}
